import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { rewriteSearchQuery, rewriteSearchUrl } from '@theme/search-query-rewriter';
import { resolveBrandCollectionUrl } from '@theme/brand-collection-url';
import { isSearchApiEnabled } from '@theme/search-api-gate';
import { applyQuickSearchProducts, fetchQuickSearchProducts } from '@theme/predictive-search-api';
import '@theme/carousel-scroll-nav';

/**
 * Maximum number of recently viewed products rendered in the search empty state.
 * Keep in sync with the paginate size in sections/predictive-search.liquid.
 */
const RECENTLY_VIEWED_DISPLAY_LIMIT = 20;

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLFormElement} form - The search form element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * @type {HTMLInputElement | null}
   */
  #tempInput = null;

  /**
   * @type {AbortController | null}
   */
  #viewportPinController = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });

      this.#patchDialogForIOSFocus(dialog);
    }

    this.addEventListener('click', this.#handleSuggestionClick, { signal });
    this.addEventListener('click', this.#handleViewAllResultsClick, { signal });
    this.refs.form?.addEventListener('submit', this.#handleFormSubmit, { signal });

    /* 모달이 열려 있는 동안 동의가 철회되면 이미 그려진 캐러셀을 걷어낸다. */
    RecentlyViewed.onReadDenied(() => {
      this.refs.recentlyViewedWrapper?.remove();
    });

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Intercepts the search form submission (Enter key without a selected
   * result, or click on the submit button) and rewrites the `q` param so
   * matching is restricted to title/product_type/vendor/tag.
   *
   * @param {SubmitEvent} event
   */
  #handleFormSubmit = (event) => {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const raw = this.refs.searchInput.value;
    const rewritten = rewriteSearchQuery(raw);

    event.preventDefault();

    // 브랜드명을 검색한 경우 /search 를 거치지 않고 브랜드 컬렉션으로 바로 보낸다.
    const brandCollectionUrl = resolveBrandCollectionUrl(raw);
    if (brandCollectionUrl) {
      window.location.href = brandCollectionUrl;
      return;
    }

    const url = new URL(form.action, location.origin);
    const params = new URLSearchParams(new FormData(form));
    params.set('q', rewritten);
    url.search = params.toString();

    window.location.href = url.toString();
  };

  /**
   * Intercepts clicks on the "view all results" anchor inside the
   * predictive search dropdown so its `q` is rewritten before navigation.
   *
   * @param {MouseEvent} event
   */
  #handleViewAllResultsClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const link = target.closest('a.predictive-search__search-button');
    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();

    // "전체 결과 보기" 도 검색 실행이므로 브랜드면 컬렉션으로 보낸다.
    const brandCollectionUrl = resolveBrandCollectionUrl(this.refs.searchInput?.value ?? '');
    if (brandCollectionUrl) {
      window.location.href = brandCollectionUrl;
      return;
    }

    window.location.href = rewriteSearchUrl(link.href);
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Handles clicks on suggestion chips via event delegation.
   *
   * Shopify 가 내려준 `resource.url` 은 재작성되지 않은 raw `q` 로 이동하므로
   * 기본 이동을 막고, 폼 submit 경로(#handleFormSubmit)로 넘겨 다른 진입점과
   * 동일한 `q` 를 만들게 한다.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleSuggestionClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const chip = target.closest('[data-suggestion-keyword]');
    if (!chip) return;

    const keyword = chip.getAttribute('data-suggestion-keyword');
    if (!keyword) return;

    event.preventDefault();

    this.refs.searchInput.value = keyword;
    this.refs.form.requestSubmit();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#tempInput) {
      this.#tempInput.remove();
      this.#tempInput = null;
    }
    this.#unpinViewport();
    this.#controller.abort();
  }

  /**
   * Patches the dialog's showDialog method to focus a temporary invisible input
   * synchronously within the user gesture chain, activating the iOS virtual keyboard.
   * @param {DialogComponent} dialog - The dialog component to patch.
   */
  #patchDialogForIOSFocus(dialog) {
    const originalShowDialog = dialog.showDialog.bind(dialog);

    dialog.showDialog = () => {
      const tempInput = document.createElement('input');
      /*
       * 화면 "안"에 두고 투명하게 숨긴다 — 예전엔 left:-9999px 로 화면 밖에 뒀다.
       *
       * iOS 는 포커스된 요소를 보이게 하려고 WKWebView scroll view 를 조정한다. 대상이 뷰포트
       * 밖(-9999px)이면 그 조정이 렌더 좌표계와 터치·캐럿 좌표계를 어긋난 상태로 남기고,
       * 곧바로 열리는 모달이 그 상태를 물려받는다 — 헤더가 그려진 곳이 아니라 그 아래를 눌러야
       * 인풋이 잡히는 증상. 뷰포트 안(0,0)에 있으면 iOS 가 스크롤할 이유가 없어 조정이 안 걸린다.
       *
       * ⚠️ font-size 는 16px 이상을 유지한다(그 아래면 iOS 가 포커스 시 화면을 확대한다).
       * ⚠️ height/width 0 은 iOS 가 포커스 대상으로 취급하지 않을 수 있어 1px 로 둔다.
       * ⚠️ opacity:0 으로만 숨긴다 — visibility/display 로 숨기면 포커스가 안 걸려 키보드가 안 뜬다.
       */
      tempInput.style.cssText =
        'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0;margin:0;font-size:16px;pointer-events:none;';
      document.body.appendChild(tempInput);
      tempInput.focus();
      this.#tempInput = tempInput;

      originalShowDialog();
    };
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#unpinViewport();
    this.#resetSearch();
    if (window.GBTrack) {
      window.GBTrack.track('search_modal_close', { section_name: 'search-modal' });
    }
  };

  /**
   * Handles the dialog open event by focusing the search input.
   */
  #handleDialogOpen = () => {
    this.#pinViewport();
    requestAnimationFrame(() => {
      this.refs.searchInput.focus();
      if (this.#tempInput) {
        this.#tempInput.remove();
        this.#tempInput = null;
      }
    });
    if (window.GBTrack) {
      window.GBTrack.track('search_modal_open', { section_name: 'search-modal' });
    }
  };

  /**
   * iOS 웹뷰에서 "그려진 위치"와 "눌리는 위치"가 어긋나는 것을 막는다.
   *
   * 실기기 계측(iOS 인앱, 검색 모달):
   *   tap client y=123 / screen y=40   → 83px 차이
   *   dialog rect y=83, input rect y=98 (화면엔 최상단에 그려짐)
   *   visualViewport.offsetTop=-83, window.scrollY=-83
   *
   * 키보드가 열리면 iOS 가 visual viewport 를 음수로 밀어낸다(문서가 `body{position:fixed}` 로
   * 스크롤 불가라 오버스크롤이 그대로 남는다). 그 상태에서 iOS 는 `position:fixed` 요소를
   * visual viewport 에 붙여 "그리지만", getBoundingClientRect·캐럿·히트테스트는 layout viewport
   * 좌표를 쓴다 — 두 좌표계가 정확히 offsetTop 만큼 벌어진다. 그래서 헤더가 보이는 곳이 아니라
   * 그 아래 83px 을 눌러야 인풋이 잡혔다.
   *
   * 문서 스크롤을 0 으로 되돌리면 두 좌표계가 다시 맞는다. body 가 이미 fixed 라 뒤 페이지는
   * 움직이지 않는다(닫을 때 dialog.js 가 원래 위치로 복원한다).
   *
   * 인풋이 없는 다이얼로그(필터·장바구니 드로어)는 키보드가 안 열려 이 문제가 없다 —
   * 그래서 dialog.js 공통이 아니라 검색 모달에만 둔다.
   */
  #pinViewport() {
    this.#unpinViewport();

    const controller = new AbortController();
    this.#viewportPinController = controller;
    const { signal } = controller;

    const pin = () => {
      /*
       * ⚠️ 닫힐 때는 개입하지 않는다. dialog.js 의 #closeDialogInternal 은
       *    body 고정 해제 → window.scrollTo(previousScrollY) → dialog.close()
       *    → DialogCloseEvent 순서라, 복원 스크롤이 DialogCloseEvent(=리스너 해제)보다
       *    먼저 일어난다. 그때 끼어들면 원래 스크롤 위치를 0 으로 되돌려 버린다.
       *    body 가 fixed 인 동안(=모달이 잡고 있는 동안)만 동작하게 막는다.
       */
      if (document.body.style.position !== 'fixed') return;
      // scrollY 가 0 이 아니면 두 좌표계가 그만큼 벌어져 있다는 뜻이다
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };

    // 키보드 등장/전환은 visualViewport 의 resize·scroll 로 온다
    window.visualViewport?.addEventListener('resize', pin, { signal });
    window.visualViewport?.addEventListener('scroll', pin, { signal });
    // 포커스 이동마다 iOS 가 다시 "보이게" 스크롤할 수 있다
    window.addEventListener('scroll', pin, { passive: true, signal });
    this.addEventListener('focusin', pin, { signal });

    pin();
  }

  /**
   * 모달이 닫힐 때 뷰포트 고정 리스너를 정리한다.
   */
  #unpinViewport() {
    this.#viewportPinController?.abort();
    this.#viewportPinController = null;
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          this.refs.form?.requestSubmit();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    /*
     * 래퍼를 페이드아웃시킨 뒤 제거한다. `removing` 이 자식 카드의 opacity·transform 을 바꾸고,
     * onAnimationEnd 가 그 transition 이 끝나기를 기다린다(진행 중인 애니메이션이 없으면 즉시 실행).
     *
     * 이전에는 recentlyViewedItems / recentlyViewedTitle ref 가 비어 있으면 조기 return 했는데,
     * 그 두 ref 를 선언하는 마크업이 없어 항상 걸렸다 — localStorage 만 비고 캐러셀이 화면에 남았다.
     */
    const { recentlyViewedWrapper } = this.refs;
    if (!recentlyViewedWrapper) {
      return;
    }

    recentlyViewedWrapper.classList.add('removing');

    onAnimationEnd(recentlyViewedWrapper, () => {
      recentlyViewedWrapper.remove();
    });
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    /*
     * 검색 API(Algolia) 가 켜져 있으면 상품 슬롯만 그 결과로 바꾼다. 추천 검색어·컬렉션은 섹션 렌더 그대로.
     * 두 요청은 병렬이고, API 가 실패·타임아웃이면 섹션 렌더의 Shopify 상품을 그대로 쓴다(폴백).
     * 판정은 search-api-gate.js 한 곳 — docs/SEARCH-API-MIGRATION.md Phase 3.
     */
    const apiProducts = isSearchApiEnabled()
      ? fetchQuickSearchProducts(searchTerm, abortController.signal).catch(() => null)
      : null;

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then(async (resultsMarkup) => {
        if (!resultsMarkup) return;

        const page = await apiProducts;
        if (abortController.signal.aborted) return;

        const strings = page ? this.#searchApiStrings : null;
        const markup = page && strings ? applyQuickSearchProducts(resultsMarkup, page, searchTerm, strings) : resultsMarkup;

        morph(predictiveSearchResults, markup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * snippets/predictive-search.liquid 가 심은 검색 API 문구. 없거나 깨졌으면 null — 호출처는 Shopify 결과를 쓴다.
   * @returns {import('@theme/predictive-search-api').QuickSearchStrings | null}
   */
  get #searchApiStrings() {
    const element = this.querySelector('script[data-search-api-strings]');
    if (!element?.textContent) return null;
    try {
      return JSON.parse(element.textContent);
    } catch {
      return null;
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   *
   * 판독 자체가 preferences 동의 대상이라 저장소를 읽기 전에 게이트를 통과시킨다.
   * 동의가 없거나 보여줄 게 없거나 조회가 실패하면 null 을 돌려주고, 호출처는
   * 캐러셀만 빼고 빈 화면을 그대로 그린다.
   *
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) {
      return null;
    }
    if (!(await RecentlyViewed.whenReadAllowed())) {
      return null;
    }

    /** Storage keeps far more ids than we render, so only the most recent ones go into the query. */
    const viewedProducts = RecentlyViewed.getProducts().slice(0, RECENTLY_VIEWED_DISPLAY_LIMIT);
    if (viewedProducts.length === 0) {
      return null;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    try {
      return await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
    } catch {
      /* 조회 실패가 빈 화면 렌더까지 막지 않게 한다. */
      return null;
    }
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    /**
     * 빈 화면과 최근 본 상품을 나란히 받는다. 최근 본 상품은 동의 확정을 기다릴 수 있어
     * 순서대로 하면 그 대기가 빈 화면 렌더 뒤에 얹히는데, 병렬로 두면 겹쳐서 사라진다.
     *
     * 둘 다 await 하고 .then 을 쓰지 않는 이유는, #closeResults 가 불릴 때 DOM 이 이미
     * morph 돼 있어야 높이 애니메이션이 동작하기 때문이다.
     */
    const [emptySectionMarkup, recentlyViewedMarkup] = await Promise.all([
      sectionRenderer.getSectionHTML(emptySectionId, false, url),
      this.#getRecentlyViewedProductsMarkup(),
    ]);

    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /* 최근 본 상품이 없거나(동의 없음·0건·조회 실패) 붙일 자리가 없으면 빈 화면만 그린다. */
    const recentlyViewedProductsHtml = recentlyViewedMarkup
      ? new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html').getElementById('recently-viewed-products')
      : null;

    /**
     * 추천검색어 바로 아래(= 추천상품 섹션 위)에 넣는다.
     * 섹션 안에 넣지 않고 형제로 두는 이유는 캐러셀이 좌우 패딩을 직접 관리해야 하기 때문이다.
     */
    const resultsInner = parsedEmptySectionMarkup.querySelector('[data-search-results]');
    if (recentlyViewedProductsHtml && resultsInner) {
      const recommendedSection = resultsInner.querySelector('#predictive-search-products');
      if (recommendedSection) {
        recommendedSection.before(recentlyViewedProductsHtml);
      } else {
        resultsInner.append(recentlyViewedProductsHtml);
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}

class SearchRecommendRandomComponent extends HTMLElement {
  #observer = null;

  connectedCallback() {
    this.#observer = new MutationObserver(() => this.#maybeShuffle());
    this.#observer.observe(this, { childList: true });
    this.#maybeShuffle();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #maybeShuffle() {
    const displayCount = parseInt(this.dataset.displayCount, 10) || 0;
    const children = Array.from(this.children);

    if (!displayCount || children.length <= displayCount) return;

    for (let i = children.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = children[i];
      children[i] = children[j];
      children[j] = temp;
    }
    children.forEach((child) => this.appendChild(child));
    children.slice(displayCount).forEach((child) => child.remove());
  }
}

if (!customElements.get('search-recommend-random')) {
  customElements.define('search-recommend-random', SearchRecommendRandomComponent);
}
